import { css } from '@emotion/core';
/* eslint frontbucket-patterns/no-new-emotion-styled: "warn" */
import styled from '@emotion/styled';

import { borderRadius, gridSize, layers } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';

// Change the margin to 8 px once we upgrade to the latest `@atlaskit/button` mentioned here:
// https://hello.atlassian.net/wiki/spaces/DPT/blog/2020/06/02/743608848/Buttons+with+icons+spacing+adjustments
export const CopyButtonWrapper = styled.div({
  marginLeft: '6px',
  minWidth: 'max-content',
});

/*
  z-index is set as such so that the [...] menu in the sticky header
  appears on top of the file header component (@atlassian/bitkit-file).
  Multiples of 10 have been used to allow for some wiggle room for
  future use if we need to tweak the layers. Even though the dropdown
  has a higher z-index than the sticky header, this works because by
  giving the file header `position:sticky`, it resets the stacking
  context, so the menu still goes underneath the sticky header.

  The stacking order is as follows using layers from @atlaskit/theme:

  Dropdown (layer() = z-index: 400
  |
  Sticky Header (navigation()+n = z-index: 220)
  |
  File Header
    dropdown closed: navigation()-n = z-index: 180
    dropdown open: navigation()-n+1 = z-index: 181

  note: subtracting 20 from navigation() so the sticky file headers
  go beneath AK Nav & blankets when mobile header is active

  note(2): AK changed the source of truth values for layers.navigation :(
  https://bitbucket.org/atlassian/atlaskit-mk-2/diff/packages/core/theme/src/constants.js?at=master&diff1=19a12541866d6444b97dec408c3c6efd5f5fbc8f&diff2=74e0f5c5708cf6db0daafa5aedf1c25f7f0da1f0
*/
const fileHeaderStackingIndex = layers.navigation() - 20;
const fileHeaderStackingIndexWhenActive = fileHeaderStackingIndex + 1;
/* prettier-ignore */
export const FileHeader = styled.div<{
  isExpanded?: boolean,
  isCollapsible?: boolean,
  isReviewed?: boolean,
  hasStickyHeader?: boolean,
  stickyHeaderOffset?: number,
  isDropdownMenuOpen?: boolean,
}>(
  (
    {
      isExpanded,
      isCollapsible,
      isReviewed
    }
  ) => ({
    alignItems: "center",
    background: token('elevation.surface.sunken'),
    border: `1px solid ${token('color.border')}`,

    borderRadius: isExpanded
      ? `${borderRadius()}px ${borderRadius()}px 0 0`
      : `${borderRadius()}px`,

    boxSizing: "border-box",
    cursor: (isCollapsible ? 'pointer' : 'auto'),
    color: (isReviewed ? token('color.text.subtle') : 'inherit'),
    display: "flex",
    flexWrap: "wrap",
    minHeight: `${gridSize() * 5}px`,
    padding: `${gridSize() / 2}px ${gridSize()}px`
  }),
  ({ hasStickyHeader }) => hasStickyHeader && `position: sticky;`,
  (
    {
      isDropdownMenuOpen
    }
  ) => ({
    top: "var(--pr-compact-header-bottom, 0)",

    zIndex: isDropdownMenuOpen
      ? `${fileHeaderStackingIndexWhenActive}`
      : `${fileHeaderStackingIndex}`,

    outline: 0,

    "&:focus": {
      outline: `2px solid ${token('color.border.focused')}`
    }
  })
);

export const FileContent = styled.div({
  border: `solid ${token('color.border')}`,
  borderWidth: '0 1px 1px',
  borderRadius: `0 0 ${`${borderRadius()}px`} ${`${borderRadius()}px`}`,
  boxSizing: 'border-box',
});

export const IconWrapper = styled.div({
  flex: 'none',
  marginLeft: `${gridSize() / 2}px`,
});

/* remove this disable once https://github.com/YozhikM/stylelint-a11y/issues/38 is addressed */
/* stylelint-disable a11y/media-prefers-reduced-motion */
export const ChevronWrapper = styled(IconWrapper)<{
  isExpanded?: boolean;
}>`
  align-self: center;
  transform: rotate(${({ isExpanded }) => (isExpanded ? 0 : -90)}deg);
  transition: all 0.15s ease;
  margin-left: 0;

  @media screen and (prefers-reduced-motion: reduce) {
    /* https://css-tricks.com/revisiting-prefers-reduced-motion-the-reduced-motion-media-query/ */
    transition-duration: 0.001ms;
  }
`;
/* stylelint-enable */

export const FileButtons = styled.div({
  display: 'inline-block',
  margin: `0 ${gridSize()}px`,
});

export const FileMenu = styled.div({
  flex: 'none',
});

/* reset Atlaskit H2 styles */
const resetHeadingStyle = css({
  font: 'inherit',
  color: 'inherit',
  margin: 0,
  padding: 0,
});

/* a11y: use h2 (semantic HTML) so that screen reader users can jump to diffs via headings */
export const FilePath = styled.h2(resetHeadingStyle, {
  wordBreak: 'break-word',
  cursor: 'auto',
  display: 'flex',
  flexWrap: 'wrap',
});

export const FilePathWrapper = styled.div({
  display: 'flex',
  alignItems: 'center',
  flex: 1,
  minWidth: `${gridSize() * 20}px`,
  marginLeft: `${gridSize()}px`,
});

export const HighlightLastWord = styled.div({
  'span:last-child': {
    fontWeight: token('font.weight.semibold'),
  },
});

export const Highlight = styled.div({
  fontWeight: token('font.weight.semibold'),
});

export const ArrowContainer = styled.div({
  marginLeft: `${gridSize() / 2}px`,
  marginRight: `-${gridSize() / 4}px`,
  height: `${gridSize() / 2}px`,
  paddingTop: `${gridSize() / 4}px`,
});

export const BeforeActions = styled.div({
  marginLeft: `${gridSize()}px`,
  display: 'flex',
  marginRight: 0,
  alignItems: 'center',
});

export const PaddedSegment = styled.span<{
  spacing: string;
}>(({ spacing }) => ({
  marginLeft: spacing,
}));

export const FileActions = styled.div({
  alignItems: 'flex-start',
  display: 'flex',
  flex: 'none',
  paddingLeft: `${gridSize()}px`,
});

export const Actions = styled.div({
  display: 'flex',
  alignItems: 'center',
  marginLeft: 'auto',
});

// AK Tooltip inserts an empty div and we need to target it
// to properly add ellipsis to the file name
export const TooltipWrapper = styled.span({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  '> div': {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
});
